var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo, useRef } from "react";
import { FormControl, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, forwardRef, useMergeRefs, useMultiStyleConfig, } from "@chakra-ui/react";
import { TextField } from "../text-field";
import { ArrowDownIcon } from "../icons";
export const Select = forwardRef((_a, ref) => {
    var { menuProps, multiple = false, isDisabled = false, options = [], value, valueKey, defaultValue, labelKey, onChange, label, placeholder = null, size, isFullWidth = true, sx } = _a, rest = __rest(_a, ["menuProps", "multiple", "isDisabled", "options", "value", "valueKey", "defaultValue", "labelKey", "onChange", "label", "placeholder", "size", "isFullWidth", "sx"]);
    const internalInputRef = useRef(null);
    const styles = useMultiStyleConfig("Select", { size });
    const resolveOptionValue = useCallback((option) => {
        return valueKey && option ? option[valueKey] : option ? option : "";
    }, [valueKey]);
    const resolveOptionLabel = useCallback((option) => {
        return labelKey ? option[labelKey] : option ? option : "";
    }, [labelKey]);
    const onOptionChange = useCallback((selected) => {
        if (multiple && Array.isArray(selected)) {
            const selectedOptions = options.filter((option) => selected.includes(resolveOptionValue(option)));
            if (onChange) {
                onChange(selectedOptions);
            }
        }
        else {
            const selectedOption = options.find((option) => resolveOptionValue(option) === selected);
            if (onChange) {
                onChange(selectedOption);
            }
        }
    }, [options, multiple, onChange, resolveOptionValue]);
    const selectedValue = useMemo(() => {
        if (!value) {
            return "";
        }
        if (multiple && Array.isArray(value)) {
            return value.map((option) => resolveOptionValue(option));
        }
        else {
            return resolveOptionValue(value);
        }
    }, [value, multiple, resolveOptionValue]);
    const selectedLabel = useMemo(() => {
        if (defaultValue && !value) {
            return resolveOptionLabel(defaultValue);
        }
        if (!value) {
            return "";
        }
        if (multiple && Array.isArray(value)) {
            return value.map((option) => resolveOptionLabel(option)).join(", ");
        }
        else {
            return resolveOptionLabel(value);
        }
    }, [value, multiple, resolveOptionLabel, defaultValue]);
    const onCloseMenu = useCallback(() => internalInputRef.current.focus(), [
        internalInputRef,
    ]);
    const refs = useMergeRefs(internalInputRef, ref);
    return (React.createElement(FormControl, null,
        React.createElement(Menu, Object.assign({ onClose: onCloseMenu, closeOnSelect: !multiple, defaultIsOpen: false, matchWidth: true, modifiers: [
                {
                    name: "preventOverflow",
                    options: {
                        altAxis: true,
                    },
                },
            ], size: size }, menuProps),
            React.createElement(MenuButton, { width: isFullWidth ? "100%" : "inherit", type: "button", disabled: isDisabled },
                React.createElement(TextField, Object.assign({ isDisabled: isDisabled, isTruncated: true, sx: Object.assign(Object.assign({}, styles.root), sx), rightIcon: !isDisabled && React.createElement(ArrowDownIcon, null), justifyContent: "flex-start", variant: "outline", value: selectedLabel, ref: refs, placeholder: placeholder, isReadOnly: true, label: label, size: size }, rest))),
            React.createElement(MenuList, { zIndex: 20, maxHeight: "100vh", overflowY: "auto" },
                React.createElement(MenuOptionGroup, { defaultValue: resolveOptionValue(defaultValue), type: multiple ? "checkbox" : "radio", onChange: onOptionChange, value: selectedValue },
                    placeholder && (React.createElement(MenuItemOption, { isDisabled: true }, placeholder)),
                    options.map((option, i) => (React.createElement(MenuItemOption, { key: i, value: resolveOptionValue(option) }, resolveOptionLabel(option)))))))));
});
